import * as types from "../Actions/Types";
import { IConfigurableReport } from "../Contracts/IConfigurableReport";
import { AnalysisSource } from "../Enums/AnalysisSource";
import { AnalysisStatus } from "../Enums/AnalysisStatus";
import { IAnalysisItem } from "../Models/IAnalysisItem";
import { IProcessReportsResult } from "../Models/IProcessReportsResult";
import { ReportType } from "../Models/IReportType";
import { IFile, IGCPBigQueryJob, IReportType } from "../Models/Models";
import {
    mapConfigurableReportsToAnalysisItems,
    mapGcpJobsToAnalysisItems,
} from "../Util/ReportUtil";

export interface IReportReducerState {
    allReports: IAnalysisItem[];
    loadingReports: boolean;
    loadingConfigurableReports: boolean;
    showConfigurableReportsModal: boolean;
    crSelectedItems: null | string;
    selectedReportId: number;
    foundReports: boolean;
    collaboratingReports: boolean;
    selectedFile: IFile;
    showFileModal: boolean;
    selectedReportFiles: IFile[];
    showRecentReportsModal: boolean;
    reportsStartDate: Date;
    reportsEndDate: Date;
    unviewedReportCount: number;
    uniqueReportTypes: string[];
    configReportsLoadedAtLeastOnce: boolean;
}

export const defaultState: IReportReducerState = {
    allReports: [],
    loadingReports: true,
    loadingConfigurableReports: false,
    showConfigurableReportsModal: false,
    crSelectedItems: null,
    selectedReportId: null,
    foundReports: true,
    collaboratingReports: false,
    selectedFile: null,
    showFileModal: false,
    selectedReportFiles: [],
    showRecentReportsModal: false,
    reportsStartDate: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    reportsEndDate: new Date(),
    unviewedReportCount: 0,
    uniqueReportTypes: [],
    configReportsLoadedAtLeastOnce: false,
};

export const reports = (
    state: IReportReducerState = { ...defaultState },
    action
): IReportReducerState => {
    switch (action.type) {
        /** misc */
        case types.SET_REPORTS_DATES: {
            const incomingStartDate: Date = action.data.startDate;
            const incomingEndDate: Date = action.data.endDate;
            return {
                ...state,
                reportsStartDate: incomingStartDate,
                reportsEndDate: incomingEndDate,
            };
        }
        case types.SET_LOADING_REPORTS: {
            return {
                ...state,
                loadingReports: action.data,
            };
        }
        case types.SET_FOUND_REPORTS: {
            return {
                ...state,
                foundReports: action.data,
            };
        }
        case types.SET_REPORTS_COLLABORATING: {
            return {
                ...state,
                collaboratingReports: action.data,
            };
        }
        case types.SET_SELECTED_REPORT_ID: {
            return {
                ...state,
                selectedReportId: action.data,
            };
        }
        case types.SET_REPORT_FILE: {
            return {
                ...state,
                selectedFile: action.data,
            };
        }
        case types.SET_SELECTED_REPORT_FILES: {
            return {
                ...state,
                selectedReportFiles: action.data,
            };
        }
        case types.SET_SHOW_REPORT_FILE_MODAL: {
            return {
                ...state,
                showFileModal: action.data,
            };
        }
        case types.SET_SHOW_RECENT_REPORTS_MODAL: {
            return {
                ...state,
                showRecentReportsModal: action.data,
            };
        }
        case types.RESET_REPORT_COUNTER: {
            return {
                ...state,
                unviewedReportCount: 0,
            };
        }
        /** success */
        case types.SET_REPORTS: {
            return {
                ...state,
                ...action.data as IProcessReportsResult,
            };
        }
        case types.GET_JOBS_SUCCESS: {
            // Filter out all reports that are sourced from Insights or RetailMatch
            const nonGcpReports: IAnalysisItem[] = [...state.allReports].filter(
                (report: IAnalysisItem) => {
                    return report.source !== AnalysisSource.Insight && report.source !== AnalysisSource.RetailMatch;
                }
            );

            // Map the GCP jobs to analysis items
            const newReports: IAnalysisItem[] = mapGcpJobsToAnalysisItems(
                action.data as IGCPBigQueryJob[]
            );

            // Append the new reports to the existing reports
            const newAllReports = [...nonGcpReports, ...newReports];

            return {
                ...state,
                allReports: newAllReports,
                uniqueReportTypes: getUniqueReportTypes(newAllReports),
            };
        }
        case types.ADD_RUNNING_CONFIG_REPORT_TO_LIST: {
            // this adds a temporary report creating report that will trigger
            // loading the actual reports
            return {
                ...state,
                allReports: [
                    ...state.allReports,
                    {
                        id: 42,
                        jobId: "42",
                        createdByUserId: 42,
                        cid: 42,
                        startDateTime: new Date(),
                        endDateTime: new Date(),
                        createdDateTime: new Date(),
                        source: AnalysisSource.Buxton,
                        studyAreaCount: 42,
                        status: AnalysisStatus.Pending,
                        name: "Report Creating...",
                        type: new ReportType("Buxton"),
                    },
                ],
            };
        }
        case types.SET_CR_MODAL_VISIBILITY: {
            return {
                ...state,
                showConfigurableReportsModal: action.data,
            };
        }
        case types.SET_CR_SELECTED_ITEMS: {
            return {
                ...state,
                crSelectedItems: action.data,
            };
        }
        case types.GET_CONFIG_REPORTS_SUCCESS: {
            // Filter out all Buxton-sourced reports
            const nonConfigurableReports: IAnalysisItem[] = [...state.allReports].filter(
                (report: IAnalysisItem) => {
                    return report.source !== AnalysisSource.Buxton;
                }
            );

            const existingConfigReports = state.allReports
                .filter(report => report.source === AnalysisSource.Buxton
                    && report.status === AnalysisStatus.Complete
                );

            const configReportsThatHaveBeenSeen = existingConfigReports
                .reduce((a, e) => {
                    a[e.id] = e.status;
                    return a;
                }, {});

            const hasFourtyTwo = state.allReports.filter(report => report.id === 42).length > 0;

            // Map the configurable reports to analysis items
            const newConfigurableReports: IAnalysisItem[] = mapConfigurableReportsToAnalysisItems(
                action.data as IConfigurableReport[]
            );

            let newCount = hasFourtyTwo

                ? newConfigurableReports.filter(
                    report => (
                        // yes, this should be equality with undefined
                        configReportsThatHaveBeenSeen[report.id] === undefined
                        && report.status === AnalysisStatus.Complete
                    ),
                ).length

                : newConfigurableReports.filter(
                    report => (
                        !!configReportsThatHaveBeenSeen[report.id]
                        && report.status === AnalysisStatus.Complete),
                ).length;


            // if they haven't been loaded, they aren't new 
            if (!state.configReportsLoadedAtLeastOnce) {
                newCount = 0;
            }

            // Append the new reports to the existing reports
            // This will also filter out any temporary reports added above
            const newAllReports = [...nonConfigurableReports, ...newConfigurableReports];

            return {
                ...state,
                allReports: newAllReports,
                uniqueReportTypes: getUniqueReportTypes(newAllReports),
                unviewedReportCount: state.unviewedReportCount + newCount,
                configReportsLoadedAtLeastOnce: true,
            };
        }
        case types.SET_LOADING_CONFIG_REPORTS: {
            return {
                ...state,
                loadingConfigurableReports: action.data,
            };
        }
        case types.DELETE_CONFIG_REPORT_SUCCESS:
        case types.DELETE_REPORT_SUCCESS: {
            const deletedReportId: number = action.data;
            const allReportsCopy: IAnalysisItem[] = removeReportById(state.allReports, deletedReportId);

            return {
                ...state,
                allReports: allReportsCopy,
                uniqueReportTypes: getUniqueReportTypes(allReportsCopy),
            };
        }
        default: {
            return state;
        }
    }
};

const removeReportById = (allReports: IAnalysisItem[], reportId: number): IAnalysisItem[] => {
    // Make copy of reports, and then return the filtered copy
    return [...allReports].filter((report: IAnalysisItem) => {
        return report.id !== reportId;
    });
};

/** helper */
export const getUniqueReportTypes = (allReports: IAnalysisItem[]): string[] => {
    const uniqueReportTypes: string[] = [];
    const uniqueTypes: IReportType[] = [];
    for (const report of allReports) {
        if (!uniqueReportTypes.includes(report.type.name)) {
            uniqueReportTypes.push(report.type.name);
            uniqueTypes.push(report.type);
        }
    }
    // Delay execution so we don't fire another redux action from a reducer
    // TODO: Move this interop call out of the reducer completely.
    // Side effects should not happen in reducers, especially if the side
    // effect is essentially triggering another action.
    setTimeout(() => {
        (window as any).scout.interop.api.reports.setReportTypesFromReports(
            uniqueTypes
        );
    });

    return uniqueReportTypes;
};
